import { Box, Group, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { CSSProperties, ReactElement } from 'react';
import { QUEUE_EMPTY_COLOR } from '../../components/QueueCells';
import { WonCoverageSummary, WonLocationCount, WonSummary as WonSummaryData } from '../../services/wonSummary';

/** The Won board (BAR-2806): the total, a breakdown and the markets. Follows the date range only. */

const BOARD_STYLE: CSSProperties = {
  padding: '12px 16px',
  background: '#fff',
  borderBottom: '1px solid rgba(0, 0, 0, 0.10)',
};

const SECONDARY_COLOR = 'rgba(0, 0, 0, 0.55)';
const SELECTED_ROW_COLOR = 'rgba(36, 55, 33, 0.06)';

function Count({ value, bold }: { value: number; bold?: boolean }): ReactElement {
  return (
    <Text component="span" fz={12} fw={bold ? 600 : 400} c={value === 0 ? QUEUE_EMPTY_COLOR : 'black'}>
      {value.toLocaleString('en-US')}
    </Text>
  );
}

interface BoardRow {
  key: string;
  label: string;
  counts: number[];
  /** Every count bold, as a subtotal reads. */
  bold?: boolean;
  tinted?: boolean;
}

interface WonBoardProps {
  total: number;
  header: string;
  columns: string[];
  rows: BoardRow[];
  footer: { label: string; counts: number[] };
  byLocation: WonLocationCount[];
}

/** The last count of each row is its total, always bold. */
function WonBoard({ total, header, columns, rows, footer, byLocation }: WonBoardProps): ReactElement {
  return (
    <Group align="flex-start" gap={40} wrap="nowrap" style={BOARD_STYLE}>
      <Stack gap={0} align="center">
        <Text fz={32} fw={600} lh="40px">
          {total.toLocaleString('en-US')}
        </Text>
        <Text fz={12} c={SECONDARY_COLOR}>
          won
        </Text>
      </Stack>

      <Table withRowBorders={false} verticalSpacing={2} horizontalSpacing="xs" fz={12} style={{ width: 'auto' }}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th fw={500} c={SECONDARY_COLOR}>
              {header}
            </Table.Th>
            {columns.map((column) => (
              <Table.Th key={column} fw={500} c={SECONDARY_COLOR} ta="right">
                {column}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.key} bg={row.tinted ? SELECTED_ROW_COLOR : undefined}>
              <Table.Td fw={row.bold ? 600 : undefined}>{row.label}</Table.Td>
              {row.counts.map((count, index) => (
                <Table.Td key={columns[index]} ta="right">
                  <Count value={count} bold={row.bold || index === row.counts.length - 1} />
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
        <Table.Tfoot style={{ borderTop: '1px solid rgba(0, 0, 0, 0.10)' }}>
          <Table.Tr>
            <Table.Td fw={600}>{footer.label}</Table.Td>
            {footer.counts.map((count, index) => (
              <Table.Td key={columns[index]} ta="right">
                <Count value={count} bold />
              </Table.Td>
            ))}
          </Table.Tr>
        </Table.Tfoot>
      </Table>

      <Box>
        <Text fz={12} fw={500} c={SECONDARY_COLOR} mb={4}>
          Location
        </Text>
        <SimpleGrid cols={2} spacing={24} verticalSpacing={2}>
          {byLocation.map(({ location, count }) => (
            <Group key={location} justify="space-between" gap={12} wrap="nowrap">
              <Text fz={12}>{location}</Text>
              <Count value={count} />
            </Group>
          ))}
        </SimpleGrid>
      </Box>
    </Group>
  );
}

export interface WonSummaryProps {
  summary: WonSummaryData;
  /** The PN filter's value; that PN's row is tinted. */
  selectedPn: string;
}

/** Intake's board: PN × track. */
export function WonSummary({ summary, selectedPn }: WonSummaryProps): ReactElement {
  return (
    <WonBoard
      total={summary.total}
      header="Patient navigator"
      columns={['Self-pay', 'Employer', 'Insurance', 'Total']}
      rows={summary.byPn.map((pn) => ({
        key: pn.pn,
        label: pn.label,
        counts: [pn.selfPay, pn.employer, pn.insurance, pn.total],
        tinted: pn.pn === selectedPn,
      }))}
      footer={{
        label: 'All',
        counts: [summary.all.selfPay, summary.all.employer, summary.all.insurance, summary.all.total],
      }}
      byLocation={summary.byLocation}
    />
  );
}

/** The Coverage pill's board (BAR-3099): employer by administrator with a subtotal, then insurance. */
export function CoverageWonSummary({ summary }: { summary: WonCoverageSummary }): ReactElement {
  return (
    <WonBoard
      total={summary.total}
      header="Track"
      columns={['Won']}
      rows={[
        { key: 'carrum', label: 'Carrum', counts: [summary.carrum] },
        { key: 'transcarent', label: 'Transcarent', counts: [summary.transcarent] },
        { key: 'employer', label: 'Employer', counts: [summary.employer], bold: true },
        { key: 'insurance', label: 'Insurance', counts: [summary.insurance] },
      ]}
      footer={{ label: 'Total', counts: [summary.total] }}
      byLocation={summary.byLocation}
    />
  );
}
